/**
 * Discoverability Suggestion Prompt
 *
 * Injects unused high-value surfaces into Losi's system prompt so the model
 * can suggest them in conversation — no UI cards under replies, no intent regex.
 *
 * Primary responsibilities:
 * - List at most a few unused journey features for the current user
 * - Instruct Losi to suggest one only when it clearly helps
 *
 * Dependencies:
 * - feature registry + journey spine
 * - usage snapshots from discoverability DB
 */

import type { BetaFeatureKey } from '@/lib/beta-features/registry'
import {
  DISCOVERABILITY_FEATURE_KEYS,
  getDiscoverabilityFeature,
  isDiscoverabilityFeatureAvailable,
  type DiscoverabilityFeatureKey,
  type DiscoverabilityPlanGate
} from '@/lib/discoverability/features'
import type { DiscoverabilityUsageSnapshot } from '@/lib/discoverability/getDiscoverabilityPrompts'
import {
  DISCOVERABILITY_JOURNEY_STRETCH_DAYS,
  getDiscoverabilityJourneyProgress
} from '@/lib/discoverability/journey'

export const LOSI_DISCOVERY_PROMPT_SIGNATURE = 'LOSI PRODUCT DISCOVERY'

const isUsed = (
  usage: Partial<Record<DiscoverabilityFeatureKey, DiscoverabilityUsageSnapshot>>,
  featureKey: DiscoverabilityFeatureKey
) => {
  const row = usage[featureKey]
  return Boolean(
    row?.firstUsedAt || (row?.useCount ?? 0) > 0 || row?.completedOnboardingAt
  )
}

/** Cap tokens — Losi only needs the next few unused surfaces, not the catalog. */
const MAX_SUGGESTION_FEATURES = 3

export function buildLosiDiscoverySuggestionPrompt(input: {
  usage: Partial<Record<DiscoverabilityFeatureKey, DiscoverabilityUsageSnapshot>>
  accountAgeDays?: number | null
  userPlan?: DiscoverabilityPlanGate | string | null
  enabledBetaFeatures?: Partial<Record<BetaFeatureKey, boolean>> | null
}): string | null {
  const accountAgeDays = input.accountAgeDays
  if (accountAgeDays == null || accountAgeDays < 0) return null
  if (accountAgeDays > DISCOVERABILITY_JOURNEY_STRETCH_DAYS) return null

  const journey = getDiscoverabilityJourneyProgress({
    accountAgeDays,
    usage: input.usage,
    userPlan: input.userPlan,
    enabledBetaFeatures: input.enabledBetaFeatures
  })

  const unused = journey.unlockedUnusedKeys
    .filter((key) => DISCOVERABILITY_FEATURE_KEYS.includes(key))
    .filter((key) => !isUsed(input.usage, key))
    .filter((key) =>
      isDiscoverabilityFeatureAvailable(key, {
        userPlan: input.userPlan,
        enabledBetaFeatures: input.enabledBetaFeatures
      })
    )
    .slice(0, MAX_SUGGESTION_FEATURES)

  if (unused.length === 0) return null

  const lines = unused.map((key) => {
    const feature = getDiscoverabilityFeature(key)
    return `- ${feature.label}: ${feature.shortDescription}`
  })

  return `
${LOSI_DISCOVERY_PROMPT_SIGNATURE}

This user has not yet used these Losi surfaces (from product usage, not guesses):
${lines.join('\n')}

When one of them clearly helps the user's current goal, mention it naturally
in your reply (one sentence + what to do next). Rules:
- At most one surface per reply.
- Never dump a feature list or pitch after every answer.
- Never invent UI chrome, sidebar tours, or fake buttons.
- If they already declined or the goal does not need it, stay silent.
`.trim()
}

export function appendLosiDiscoverySuggestionPromptToText(
  content: string,
  block: string | null | undefined
) {
  if (!block || !block.trim()) return content
  if (content.includes(LOSI_DISCOVERY_PROMPT_SIGNATURE)) return content
  return `${content.trimEnd()}\n\n${block.trim()}`
}
